import React, { useState, useEffect } from 'react';
import ComboBox from '../../components/ComboBox';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import PoDetailModal, { PO_STATUSES, PO_STATUS_COLORS, PoShipBits } from './PoDetailModal';
import { jobLabel } from '../../utils/ui';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
function getToken() {
  try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; }
}
function apiFetch(path, opts = {}) {
  const token = getToken();
  return fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(token ? { 'x-worker-token': token } : {}), ...opts.headers },
  }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
}


const STATUS_COLORS = PO_STATUS_COLORS;
const STATUSES = PO_STATUSES;

function fmt$(n) {
  if (n == null) return '—';
  return '$' + parseFloat(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Trello-era POs were titled "Vendor - 2699 - what it's for", and the table already
// has its own Project and Vendor columns — so the row read "2699 - Shop | 3D
// Concrete, LLC - 2699 - 1230SSN | 3D Concrete, LLC", with the job number and the
// vendor each shown twice. Trim that prefix for DISPLAY only, and only when it really
// is this PO's own vendor and job number, so nothing real is ever hidden. The stored
// description is left alone — it's what was actually written at the time.
function poDescription(po) {
  let d = String(po.description || '').trim();
  const num = String(po.project_number || '').trim();
  const vendor = String(po.vendor || '').trim();
  if (vendor && d.toLowerCase().startsWith(vendor.toLowerCase())) {
    d = d.slice(vendor.length).replace(/^\s*[-–—:]\s*/, '');
  }
  if (num) {
    const m = d.match(/^\s*0*(\d{3,6})\s*[-–—:]\s*/);
    if (m && m[1] === num.replace(/\D/g, '')) d = d.slice(m[0].length);
  }
  return d.trim() || String(po.description || '').trim();
}

// Column sort (Pat, Sep 28 2026): the same clickable headers as the Projects and
// Equipment lists. The month cards stay; the order inside each one follows the header.
const PO_COLUMNS = [
  ['po_number', 'PO #'], ['project', 'Project'], ['description', 'Description'], ['vendor', 'Vendor'],
  ['cost', 'Cost'], ['by', 'Submitted By'], ['date', 'Date'], ['status', 'Status'], [null, ''],
];
const sortValue = (po, key) => {
  switch (key) {
    case 'po_number': return Number(String(po.po_number || '').replace(/\D/g, '')) || 0;
    case 'project': return (po.equipment_asset || po.equipment_name || po.project_name || '').toLowerCase();
    case 'description': return poDescription(po).toLowerCase();
    case 'vendor': return (po.vendor || '').toLowerCase();
    case 'cost': return Number(po.cost) || 0;
    case 'by': return `${po.first_name || ''} ${po.last_name || ''}`.trim().toLowerCase();
    case 'status': return (po.status || '').toLowerCase();
    default: return po.created_at || '';
  }
};
function sortPos(list, key, dir) {
  const s = dir === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    const va = sortValue(a, key), vb = sortValue(b, key);
    const c = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb));
    return (c || String(b.created_at || '').localeCompare(String(a.created_at || ''))) * (c ? s : 1);
  });
}
// The calendar day a PO was written, for the From / To range (local time, like the list).
const poDay = po => {
  const d = new Date(po.created_at);
  return Number.isNaN(d.getTime()) ? '' : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function groupByMonth(pos) {
  const map = {};
  pos.forEach(po => {
    const d = new Date(po.created_at);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    if (!map[key]) map[key] = { key, label, pos: [] };
    map[key].pos.push(po);
  });
  return Object.values(map).sort((a, b) => b.key.localeCompare(a.key));
}

function POCreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ projectId: '', description: '', vendor: '', cost: '', notes: '', is_rental: false, rental_start: '', notify_shop: false });
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch('/projects').then(data => setProjects(data || [])).catch(() => {});
    apiFetch('/vendors/active').then(data => setVendors(data || [])).catch(() => {});
  }, []);

  function field(key) {
    return { value: form[key], onChange: e => setForm(f => ({ ...f, [key]: e.target.value })) };
  }

  async function save() {
    if (!form.projectId) { setError('Project is required.'); return; }
    if (!form.description.trim()) { setError('Description is required.'); return; }
    setSaving(true);
    try {
      const result = await apiFetch('/purchase-orders', {
        method: 'POST',
        body: JSON.stringify({
          projectId: form.projectId,
          description: form.description.trim(),
          vendor: form.vendor || null,
          cost: form.cost !== '' ? parseFloat(form.cost) : null,
          notes: form.notes.trim() || null,
          is_rental: form.is_rental ? 1 : 0,
          rental_start: form.is_rental ? (form.rental_start || null) : null,
          notify_shop: form.notify_shop ? 1 : 0,
        }),
      });
      onCreated(result);
    } catch (e) { setError(e?.error || 'Failed to create PO.'); }
    setSaving(false);
  }

  return (
    // No close-on-backdrop: a stray click outside used to throw away a half-written PO. ✕ or Cancel closes it.
    <div className="admin-modal-overlay">
      <div className="admin-modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="admin-modal-header">
          <div>
            <div style={{ fontSize: 11, color: 'var(--gray-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>New Purchase Order</div>
            <h3 style={{ margin: 0, fontSize: 20, color: 'var(--orange)' }}>Create PO</h3>
          </div>
          <button className="admin-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="admin-modal-form" style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '0 24px 24px' }}>
          <div className="field-group">
            <label className="field-label">Project *</label>
            <ComboBox
              value={form.projectId ? jobLabel(projects.find(p => p.id === form.projectId) || {}) : ''}
              onChange={(val) => setForm(f => ({ ...f, projectId: val }))}
              options={projects.map(p => ({ value: p.id, label: jobLabel(p) }))}
              placeholder="Search projects…"
              freeText={false}
              noun="job"
            />
          </div>
          <div className="field-group">
            <label className="field-label">Description *</label>
            <textarea className="field-input" rows={3} style={{ resize: 'vertical' }} {...field('description')} />
          </div>
          <div className="admin-form-row">
            <div className="field-group">
              <label className="field-label">Vendor</label>
              <ComboBox
                value={form.vendor}
                onChange={(val, label) => setForm(f => ({ ...f, vendor: val || label }))}
                options={vendors.map(v => ({ value: v.name, label: v.name }))}
                placeholder="Search vendors…"
              />
            </div>
            <div className="field-group">
              <label className="field-label">Cost ($)</label>
              <input className="field-input" type="text" inputMode="decimal" placeholder="0.00" {...field('cost')} />
            </div>
          </div>
          <div className="field-group">
            <label className="field-label">Notes</label>
            <textarea className="field-input" rows={2} placeholder="Optional" style={{ resize: 'vertical' }} {...field('notes')} />
          </div>
          <div className="field-group">
            <div style={{ border: '1px solid #ede9fe', background: '#faf5ff', borderRadius: 8, padding: '10px 12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#6d28d9' }}>
                <input type="checkbox" checked={form.is_rental} onChange={e => setForm(f => ({ ...f, is_rental: e.target.checked }))} /> This PO is for rented equipment
              </label>
              {form.is_rental && (
                <div style={{ marginTop: 8 }}>
                  <label className="field-label" style={{ display: 'block' }}>Rental start date</label>
                  <input className="field-input" type="date" {...field('rental_start')} />
                  <div style={{ fontSize: 11, color: '#7c3aed', marginTop: 4 }}>Adds a card to Shop → Equipment → Rented Equipment.</div>
                </div>
              )}
            </div>
          </div>
          <div className="field-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: 'var(--text)' }}>
              <input type="checkbox" checked={form.notify_shop} onChange={e => setForm(f => ({ ...f, notify_shop: e.target.checked }))} /> Notify the shop when a ship date is set
            </label>
            <span style={{ fontSize: 11, color: 'var(--gray-muted)' }}>For big, coordination-heavy orders.</span>
          </div>
          {error && <div style={{ color: 'var(--red,#dc2626)', fontSize: 13 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button onClick={onClose} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1.5px solid var(--border)', background: '#fff', color: 'var(--text)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
            <button onClick={save} disabled={saving} style={{ flex: 2, padding: '10px 0', borderRadius: 8, border: 'none', background: saving ? '#ccc' : 'var(--orange)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? 'Creating…' : 'Create PO'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function POEditModal({ po, onClose, onSaved }) {
  const [form, setForm] = useState({
    description: po.description || '',
    vendor: po.vendor || '',
    cost: po.cost != null ? String(po.cost) : '',
    notes: po.notes || '',
    status: po.status || 'pending',
  });
  const [vendors, setVendors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch('/vendors/active').then(data => setVendors(data || [])).catch(() => {});
  }, []);

  function field(key) {
    return { value: form[key], onChange: e => setForm(f => ({ ...f, [key]: e.target.value })) };
  }

  async function save() {
    if (!form.description.trim()) { setError('Description is required.'); return; }
    setSaving(true);
    try {
      await apiFetch(`/purchase-orders/${po.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          description: form.description.trim(),
          vendor: form.vendor.trim() || null,
          cost: form.cost !== '' ? parseFloat(form.cost) : null,
          notes: form.notes.trim() || null,
          status: form.status,
        }),
      });
      onSaved({ ...po, ...form, cost: form.cost !== '' ? parseFloat(form.cost) : null, vendor: form.vendor.trim() || null });
    } catch { setError('Failed to save changes.'); }
    setSaving(false);
  }

  return (
    // No close-on-backdrop: a stray click outside used to throw away a half-written PO. ✕ or Cancel closes it.
    <div className="admin-modal-overlay">
      <div className="admin-modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="admin-modal-header">
          <div>
            <div style={{ fontSize: 11, color: 'var(--gray-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Edit Purchase Order</div>
            <h3 style={{ margin: 0, fontSize: 20, color: 'var(--orange)' }}>#{po.po_number}</h3>
            {po.project_name && <div style={{ fontSize: 13, color: 'var(--gray-muted)', marginTop: 2 }}>{po.project_name}</div>}
          </div>
          <button className="admin-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="admin-modal-form" style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '0 24px 24px' }}>
          <div className="field-group">
            <label className="field-label">Description *</label>
            <textarea className="field-input" rows={3} style={{ resize: 'vertical' }} {...field('description')} />
          </div>
          <div className="admin-form-row">
            <div className="field-group">
              <label className="field-label">Vendor</label>
              <ComboBox
                value={form.vendor}
                onChange={(val, label) => setForm(f => ({ ...f, vendor: val || label }))}
                options={vendors.map(v => ({ value: v.name, label: v.name }))}
                placeholder="Search vendors…"
              />
            </div>
            <div className="field-group">
              <label className="field-label">Cost ($)</label>
              <input className="field-input" type="text" inputMode="decimal" placeholder="0.00" {...field('cost')} />
            </div>
          </div>
          <div className="field-group">
            <label className="field-label">Status</label>
            <select className="field-input" {...field('status')}>
              {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
          </div>
          <div className="field-group">
            <label className="field-label">Notes</label>
            <textarea className="field-input" rows={2} placeholder="Optional" style={{ resize: 'vertical' }} {...field('notes')} />
          </div>
          {error && <div style={{ color: 'var(--red,#dc2626)', fontSize: 13 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button
              onClick={onClose}
              style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1.5px solid var(--border)', background: '#fff', color: 'var(--text)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
            >Cancel</button>
            <button
              onClick={save}
              disabled={saving}
              style={{ flex: 2, padding: '10px 0', borderRadius: 8, border: 'none', background: saving ? '#ccc' : 'var(--orange)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}
            >{saving ? 'Saving…' : 'Save Changes'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MonthGroup({ group, onEdit, defaultOpen, sortKey = 'date', sortDir = 'desc', onSort, dupNumbers = new Set() }) {
  const [open, setOpen] = useState(defaultOpen);
  const total = group.pos.reduce((s, p) => s + (p.cost || 0), 0);
  const rows = sortPos(group.pos, sortKey, sortDir);

  return (
    <div className="admin-card" style={{ marginBottom: 12, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', background: 'none', border: 'none', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', textAlign: 'left' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ color: 'var(--gray-muted)', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}>
            <path d="M5 3l6 5-6 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span style={{ fontWeight: 700, fontSize: 16 }}>{group.label}</span>
          <span className="admin-list-badge gray">{group.pos.length} PO{group.pos.length !== 1 ? 's' : ''}</span>
        </div>
      </button>

      {open && (
        <div style={{ borderTop: '1px solid var(--border)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: 'var(--surface-alt,#f9fafb)' }}>
                {PO_COLUMNS.map(([key, h]) => {
                  const on = key && sortKey === key;
                  return (
                    <th key={h || 'x'} onClick={key && onSort ? () => onSort(key) : undefined} title={key ? `Sort by ${h}` : undefined}
                      style={{ padding: '9px 16px', textAlign: 'left', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: on ? 'var(--orange)' : 'var(--gray-muted)', whiteSpace: 'nowrap', cursor: key ? 'pointer' : 'default', userSelect: 'none' }}>
                      {h}{on ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map(po => {
                // A status with no color (a new PO comes back as "pending") gets grey — it used to look up a
                // "pending" color that doesn't exist and blank the whole page (Amanda, Oct 26 2026).
                const sc = STATUS_COLORS[po.status] || { bg: '#f3f4f6', text: '#374151' };
                return (
                  <tr key={po.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 800, color: 'var(--orange)', fontSize: 14, whiteSpace: 'nowrap' }}>
                      {/* The orange number opens the PO card, same as the ✎ (Ryan, Oct 12 2026). */}
                      <span onClick={() => onEdit(po)} title="Open the PO" style={{ cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'rgba(232,119,34,0.35)', textUnderlineOffset: 3 }}>#{po.po_number}</span>
                      {dupNumbers.has(String(po.po_number)) && <span title="Another PO has this same number — open one of them and press ✎ beside the number to change it" style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, color: '#fff', background: '#dc2626', border: '1px solid #b91c1c', padding: '1px 6px', borderRadius: 999, verticalAlign: 'middle', letterSpacing: 0.3 }}>⚠ DUPLICATE</span>}
                      {!!po.is_major && <span title="Major PO" style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, color: '#b91c1c', background: '#fee2e2', border: '1px solid #fca5a5', padding: '1px 5px', borderRadius: 5, verticalAlign: 'middle' }}>⭐ MAJOR</span>}
                      {!!po.is_major && !!po.ndot_certs && <span title="NDOT certifications required" style={{ marginLeft: 4, fontSize: 9, fontWeight: 800, color: '#1e40af', background: '#dbeafe', border: '1px solid #93c5fd', padding: '1px 5px', borderRadius: 5, verticalAlign: 'middle' }}>NDOT CERTS</span>}
                      {po.source === 'trello' && <span title="Written in Trello, mirrored here" style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, color: '#0079bf', background: '#eaf5fb', border: '1px solid #b6e0f5', padding: '1px 5px', borderRadius: 5, verticalAlign: 'middle' }}>TRELLO</span>}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--gray-muted)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {(po.equipment_asset || po.equipment_name) ? (
                        <>
                          <div style={{ color: '#9a3412', fontWeight: 700 }} title="Charged to equipment">🔧 {po.equipment_asset || po.equipment_name}</div>
                          {po.project_name && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>{po.project_name} ledger</div>}
                        </>
                      ) : (po.project_name || '—')}
                    </td>
                    <td style={{ padding: '12px 16px', maxWidth: 220 }}>
                      <div style={{ fontWeight: 500, lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }} title={po.description}>{poDescription(po)}</div>
                      {po.notes && <div style={{ fontSize: 12, color: 'var(--gray-muted)', marginTop: 2 }}>{po.notes}</div>}
                      <PoShipBits po={po} compact />
                    </td>
                    <td style={{ padding: '12px 16px', color: 'var(--gray-muted)', whiteSpace: 'nowrap' }}>{po.vendor || '—'}</td>
                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap', fontWeight: 600 }}>{fmt$(po.cost)}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--gray-muted)', whiteSpace: 'nowrap' }}>{po.first_name} {po.last_name}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--gray-muted)', whiteSpace: 'nowrap', fontSize: 13 }}>
                      {new Date(po.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700, textTransform: 'capitalize', background: sc.bg, color: sc.text }}>
                        {po.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <button
                        onClick={() => onEdit(po)}
                        className="admin-icon-btn"
                        title="Edit PO"
                      >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M10 2l2 2-8 8H2v-2L10 2Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" fill="none"/></svg>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AdminPurchaseOrders({ embedded = false }) {
  const navigate = useNavigate();
  const Wrap = embedded ? React.Fragment : AdminLayout;
  const [pos, setPos]         = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [editingPO, setEditingPO] = useState(null);
  const [creatingPO, setCreatingPO] = useState(false);
  const [statusFilter, setStatusFilter] = useState('All');
  const [majorOnly, setMajorOnly] = useState(false);
  const [search, setSearch]   = useState('');
  const [vendors, setVendors] = useState([]);
  // Sort by any column, and a From / To range on the date written (Pat, Sep 28 2026).
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const onSort = key => { if (key === sortKey) setSortDir(d => (d === 'asc' ? 'desc' : 'asc')); else { setSortKey(key); setSortDir(key === 'date' || key === 'cost' || key === 'po_number' ? 'desc' : 'asc'); } };

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => { load(); apiFetch('/vendors').then(v => setVendors(Array.isArray(v) ? v : [])).catch(() => {}); }, []);

  // Deep-link: /admin/purchase-orders?open=<poId> opens that PO's detail directly
  // (used by the PM dashboard "POs to release" list).
  useEffect(() => {
    // …or ?po=<PO number> (the assistant's "Take me there", Oct 18 2026).
    const openId = searchParams.get('open') || searchParams.get('po');
    if (openId && pos.length) {
      const po = pos.find(p => String(p.id) === String(openId)) || pos.find(p => String(p.po_number) === String(openId).replace(/^#/, ''));
      if (po) { setEditingPO(po); setSearchParams({}, { replace: true }); }
    }
  }, [searchParams, pos]); // eslint-disable-line react-hooks/exhaustive-deps



  // quiet: refresh the list in place (after writing a PO) without the Loading… blink.
  async function load(quiet = false) {
    if (!quiet) setLoading(true);
    setError(null);
    try {
      const data = await apiFetch('/purchase-orders');
      setPos(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.error || 'Failed to load purchase orders.');
    } finally {
      setLoading(false);
    }
  }

  function handleSaved(updated) {
    setPos(ps => ps.map(p => p.id === updated.id ? { ...p, ...updated } : p));
    setEditingPO(null);
  }
  function handleDeleted(deleted) {
    setPos(ps => ps.filter(p => p.id !== deleted.id));
    setEditingPO(null);
  }

  const majorCount = pos.filter(p => p.is_major).length;
  // Numbers carried by more than one PO. Two were written 117 ms apart on Sep 30 2026 and
  // both came out #20868; the numbering no longer allows it, but anything already doubled
  // has to be seen to be fixed — open one of them and ✎ the number.
  const dupNumbers = (() => { const seen = new Map(); for (const p of pos) { const k = String(p.po_number || ''); if (k) seen.set(k, (seen.get(k) || 0) + 1); } return new Set([...seen].filter(([, n]) => n > 1).map(([k]) => k)); })();
  const filtered = pos.filter(po => {
    const matchStatus = statusFilter === 'All' || po.status === statusFilter;
    const matchMajor = !majorOnly || !!po.is_major;
    const q = search.toLowerCase();
    const matchSearch = !q || String(po.po_number).includes(q) || (po.description || '').toLowerCase().includes(q) || (po.vendor || '').toLowerCase().includes(q) || (po.project_name || '').toLowerCase().includes(q) || (`${po.first_name} ${po.last_name}`).toLowerCase().includes(q);
    const day = (from || to) ? poDay(po) : '';
    const matchDate = (!from || day >= from) && (!to || day <= to);
    return matchStatus && matchMajor && matchSearch && matchDate;
  });
  const rangeOn = !!(from || to);

  const groups = groupByMonth(filtered);
  const grandTotal = filtered.reduce((s, p) => s + (p.cost || 0), 0);
  const statusCounts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: pos.filter(p => p.status === s).length }), {});

  return (
    <Wrap>
      <div className="admin-page-header">
        <div>
          {embedded && <button onClick={() => navigate('/dashboard')} style={{ background: '#f3f4f6', border: 'none', color: '#e87722', fontSize: 14, fontWeight: 700, cursor: 'pointer', padding: '8px 14px', borderRadius: 20, marginBottom: 10, display: 'inline-flex', alignItems: 'center', gap: 6 }}>‹ Home</button>}
          <h1 className="admin-page-title">Purchase Orders</h1>
          <p className="admin-page-sub">{pos.length} total{grandTotal > 0 ? ` · ${fmt$(grandTotal)} total value` : ''}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Wedco's weekly material update → each PM OKs their POs (Ryan, Oct 4 2026). */}
          <button onClick={() => navigate('/admin/wedco-updates')} title="Upload Wedco's weekly spreadsheet; each PM OKs the ship dates for their POs"
            style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid #1e40af', background: '#eff6ff', color: '#1e40af', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>📦 Wedco update</button>
          <button
            onClick={() => setCreatingPO(true)}
            style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: 'var(--orange)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
          >+ New PO</button>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-card" style={{ marginBottom: 16 }}>
        <div style={{ padding: '16px 20px', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            className="field-input"
            placeholder="Search PO#, project, vendor, description…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 220 }}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            {['All', ...STATUSES].map(s => (
              <button
                key={s}
                className={`role-tab ${statusFilter === s ? 'active' : ''}`}
                onClick={() => setStatusFilter(s)}
                style={{ textTransform: 'capitalize' }}
              >
                {s}{s !== 'All' ? ` (${statusCounts[s] || 0})` : ` (${pos.length})`}
              </button>
            ))}
            <button
              onClick={() => setMajorOnly(v => !v)}
              title="Show only Major POs"
              style={{ padding: '6px 12px', borderRadius: 20, border: `1.5px solid ${majorOnly ? '#b91c1c' : '#fca5a5'}`, background: majorOnly ? '#b91c1c' : '#fef2f2', color: majorOnly ? '#fff' : '#b91c1c', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}
            >⭐ Major only ({majorCount})</button>
          </div>
        </div>
        <div style={{ padding: '0 20px 14px', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--gray-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Written between</span>
          <input type="date" className="field-input" value={from} max={to || undefined} onChange={e => setFrom(e.target.value)} style={{ width: 160 }} title="From this day" />
          <span style={{ fontSize: 13, color: 'var(--gray-muted)' }}>and</span>
          <input type="date" className="field-input" value={to} min={from || undefined} onChange={e => setTo(e.target.value)} style={{ width: 160 }} title="Up to and including this day" />
          {rangeOn && (
            <>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>{filtered.length} PO{filtered.length === 1 ? '' : 's'}{grandTotal > 0 ? ` · ${fmt$(grandTotal)}` : ''}</span>
              <button onClick={() => { setFrom(''); setTo(''); }} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 20, padding: '5px 11px', fontSize: 12, fontWeight: 700, color: '#374151', cursor: 'pointer' }}>× Clear dates</button>
            </>
          )}
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 11, color: 'var(--gray-muted)' }}>Sorted by {(PO_COLUMNS.find(([k]) => k === sortKey) || [])[1] || 'Date'} {sortDir === 'asc' ? '▲' : '▼'} — tap a column heading to change it</span>
        </div>
      </div>

      {loading && <div className="admin-empty">Loading…</div>}
      {error && <div className="admin-empty" style={{ color: 'var(--red,#dc2626)' }}>{error}</div>}

      {!loading && !error && groups.length === 0 && (
        <div className="admin-empty">No purchase orders found.</div>
      )}

      {!loading && dupNumbers.size > 0 && (
        <div style={{ margin: '0 0 12px', background: '#fef2f2', border: '1.5px solid #fecaca', borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 22 }}>⚠</span>
          <div style={{ fontSize: 13, color: '#991b1b' }}>
            <strong>{dupNumbers.size === 1 ? 'A PO number is' : `${dupNumbers.size} PO numbers are`} used more than once:</strong> {[...dupNumbers].join(', ')}.
            {' '}Open one of each pair and press ✎ beside its number to give it a new one.
          </div>
        </div>
      )}
      {!loading && groups.map((g, i) => (
        <MonthGroup key={g.key} group={g} onEdit={setEditingPO} defaultOpen={i === 0 || rangeOn} sortKey={sortKey} sortDir={sortDir} onSort={onSort} dupNumbers={dupNumbers} />
      ))}

      {creatingPO && (
        <POCreateModal
          onClose={() => setCreatingPO(false)}
          onCreated={po => { setPos(ps => [po, ...ps]); setCreatingPO(false); load(true); }}
        />
      )}
      {editingPO && (
        <PoDetailModal po={editingPO} vendors={vendors} onClose={() => setEditingPO(null)} onSaved={handleSaved} onDeleted={handleDeleted} />
      )}
    </Wrap>
  );
}
